import { useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { ArrowDownCircle, ArrowUpCircle, Plus, WalletCards } from "lucide-react";
import { Link } from "react-router-dom";
import { Card, EmptyState, InlineNotice, Money, PageHeader, StatusBadge } from "../components/ui";
import { db } from "../lib/db";
import { useAuth } from "../lib/auth";
import { api, ApiError } from "../lib/api";
import { applyLocalCash } from "../lib/local-operations";
import { flushSyncQueue } from "../lib/sync";
import { refreshFromServer } from "../lib/data";
import { formatDateTime } from "../lib/format";

export function CashBookPage() {
  const auth = useAuth();
  const movements = useLiveQuery(() => db.money.orderBy("createdAt").reverse().toArray(), [], []);
  const dashboard = useLiveQuery(async () => (await db.meta.get("dashboard"))?.value as any, []);
  const [showForm, setShowForm] = useState(false);
  const [direction, setDirection] = useState<"IN" | "OUT">("OUT");
  const [amount, setAmount] = useState(0);
  const [category, setCategory] = useState("Chi phí khác");
  const [accountType, setAccountType] = useState<"CASH" | "BANK">("CASH");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const cash = dashboard?.accounts?.find((a: any) => a.type === "CASH")?.balance ?? 0;
  const bank = dashboard?.accounts?.find((a: any) => a.type === "BANK")?.balance ?? 0;

  async function submit() {
    if (amount <= 0) return;
    setBusy(true);
    setError("");
    const idempotencyKey = crypto.randomUUID();
    const payload = {
      idempotencyKey,
      direction,
      amount: Math.round(amount),
      category,
      accountType,
    };

    try {
      if (auth.demo || !navigator.onLine) {
        await applyLocalCash(
          direction,
          amount,
          category,
          accountType,
          idempotencyKey,
          undefined,
          {
            demo: auth.demo,
            tenantId: auth.session?.tenant?.id,
            storeId: auth.session?.store?.id,
          },
        );
      } else {
        try {
          await api("/cash/movements", {
            method: "POST",
            body: JSON.stringify(payload),
          });
          await refreshFromServer();
        } catch (onlineError) {
          if (onlineError instanceof ApiError && onlineError.status < 500) {
            throw onlineError;
          }

          await applyLocalCash(
            direction,
            amount,
            category,
            accountType,
            idempotencyKey,
            undefined,
            {
              demo: false,
              tenantId: auth.session?.tenant?.id,
              storeId: auth.session?.store?.id,
            },
          );
          const retry = await flushSyncQueue(
            auth.session?.tenant?.id,
            auth.session?.store?.id,
          );
          if (retry.failed === 0 && retry.pendingRemaining === 0) {
            await refreshFromServer();
          }
        }
      }

      setShowForm(false);
      setAmount(0);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Không thể lưu khoản thu/chi.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page-stack">
      <PageHeader title="Sổ tiền" description="Theo dõi tiền vào, tiền ra và số dư" action={<button className="btn btn-primary" onClick={() => { setError(""); setShowForm(true); }}><Plus size={18} /> Ghi thu / chi</button>} />

      {error ? <InlineNotice tone="danger" title={error} /> : null}

      <InlineNotice tone="info" title="Công nợ được ghi ở sổ riêng">
        Thu nợ khách hàng hoặc trả nợ nhà cung cấp phải thực hiện tại <Link to="/debts"><strong>Công nợ</strong></Link> để tiền và số dư phải thu/phải trả được cập nhật cùng lúc.
      </InlineNotice>

      <div className="metric-grid">
        <Card><WalletCards size={20} /><span>Két tiền mặt</span><Money value={cash} /></Card>
        <Card><WalletCards size={20} /><span>Ngân hàng</span><Money value={bank} /></Card>
      </div>

      <section>
        <div className="section-title"><div><h2>Giao dịch gần đây</h2></div></div>
        {movements.length === 0 ? (
          <EmptyState title="Chưa có giao dịch" description="Ghi một khoản thu hoặc chi để bắt đầu." />
        ) : (
          <Card className="transaction-list">
            {movements.map((movement) => (
              <div className="transaction-row" key={movement.id}>
                <span className={`transaction-icon ${movement.direction === "IN" ? "in" : "out"}`}>
                  {movement.direction === "IN" ? <ArrowDownCircle /> : <ArrowUpCircle />}
                </span>
                <div><strong>{movement.category}</strong><small>{formatDateTime(movement.createdAt)} · {movement.accountType === "CASH" ? "Tiền mặt" : "Ngân hàng"}</small></div>
                <div className="transaction-amount">
                  <Money value={(movement.direction === "IN" ? 1 : -1) * movement.amount} />
                  {movement.syncStatus === "PENDING" ? <StatusBadge tone="info">Chờ đồng bộ</StatusBadge> : null}
                </div>
              </div>
            ))}
          </Card>
        )}
      </section>

      {showForm ? (
        <div className="modal-backdrop" onMouseDown={() => !busy && setShowForm(false)}>
          <div className="bottom-sheet" onMouseDown={(e) => e.stopPropagation()}>
            <div className="sheet-handle" />
            <h2>Ghi thu / chi</h2>
            <div className="segmented">
              <button className={direction === "IN" ? "active" : ""} onClick={() => { setDirection("IN"); setCategory("Thu khác"); }}>+ Thu tiền</button>
              <button className={direction === "OUT" ? "active" : ""} onClick={() => { setDirection("OUT"); setCategory("Chi phí khác"); }}>− Chi tiền</button>
            </div>
            <label>Số tiền<input className="currency-field" type="number" inputMode="numeric" value={amount || ""} onChange={(e) => setAmount(Number(e.target.value))} /></label>
            <label>Danh mục
              <select value={category} onChange={(e) => setCategory(e.target.value)}>
                {direction === "IN"
                  ? <><option>Thu khác</option></>
                  : <><option>Chi phí khác</option><option>Điện</option><option>Nước</option><option>Internet</option><option>Vận chuyển</option><option>Thuê mặt bằng</option><option>Lương</option></>}
              </select>
            </label>
            <label>Nguồn tiền
              <select value={accountType} onChange={(e) => setAccountType(e.target.value as "CASH" | "BANK")}>
                <option value="CASH">Tiền mặt</option><option value="BANK">Ngân hàng</option>
              </select>
            </label>
            <button className="btn btn-primary btn-lg" disabled={busy || amount <= 0} onClick={() => void submit()}>{busy ? "Đang lưu..." : direction === "IN" ? "Lưu khoản thu" : "Lưu khoản chi"}</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
